import { Fragment, memo, useCallback, useMemo, useRef } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { Checkbox } from '@nesso/ui/primitives/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@nesso/ui/primitives/command';
import { Separator } from '@nesso/ui/primitives/separator';

import { useOptionsGroups } from '@/shared/lib/use-options-groups';

type Option = { value: string; label: string; group?: string; keywords?: string[] };

interface Props {
    options: Option[];
    selected: string[];
    onChange: (selected: string[]) => void;
    minSelected?: number;
    maxSelected?: number;
    searchPlaceholder?: string;
    notFoundMessage?: string;
    inputLast?: boolean;
    disabled?: boolean;
    className?: string;
    inputWrapperClassName?: string;
    emptyWrapperClassName?: string;
    commandListClassName?: string;
    commandGroupClassName?: string;
    groupOrder?: string[];
}

type ItemProps = {
    value: string;
    label: string;
    extraKeywords?: string[];
    isSelected: boolean;
    isDisabled: boolean;
    onToggle: (value: string) => void;
};

const SearchableCheckboxItem = memo(function SearchableCheckboxItem({
    value,
    label,
    extraKeywords,
    isSelected,
    isDisabled,
    onToggle,
}: ItemProps) {
    const handleSelect = useCallback(() => onToggle(value), [onToggle, value]);
    const keywords = useMemo(() => [label, ...(extraKeywords ?? [])], [label, extraKeywords]);
    return (
        <CommandItem
            value={value}
            keywords={keywords}
            onSelect={handleSelect}
            disabled={isDisabled}
            className={cn(
                'h-10 gap-3 px-4 hover:bg-accent/50 data-[selected=true]:hover:bg-accent/50 data-[selected=true]:bg-transparent data-[selected=true]:text-foreground',
                isSelected && 'bg-accent/50!',
            )}
        >
            {/** biome-ignore lint/a11y/noStaticElementInteractions: blocks synthetic clicks dispatched by Radix Checkbox's bubble input from re-triggering cmdk's onSelect */}
            <span
                className="flex items-center h-4 w-4"
                onClick={(e) => {
                    if (!e.nativeEvent.isTrusted) e.stopPropagation();
                }}
            >
                <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    className="pointer-events-none"
                />
            </span>
            <span className="text-sm">{label}</span>
        </CommandItem>
    );
});

export function SearchableCheckboxList({
    options,
    selected,
    onChange,
    minSelected,
    maxSelected,
    searchPlaceholder = 'Search',
    notFoundMessage = 'No options found.',
    inputLast = false,
    disabled = false,
    className,
    inputWrapperClassName,
    emptyWrapperClassName,
    commandListClassName,
    commandGroupClassName,
    groupOrder,
}: Props) {
    const { ungrouped, groups } = useOptionsGroups(options, groupOrder);

    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const handleToggle = useCallback(
        (value: string) => {
            const current = selectedRef.current;
            const isSelected = current.includes(value);
            if (isSelected) {
                if (minSelected && current.length <= minSelected) return;
                onChangeRef.current(current.filter((v) => v !== value));
            } else {
                if (maxSelected && current.length >= maxSelected) return;
                onChangeRef.current([...current, value]);
            }
        },
        [minSelected, maxSelected],
    );

    const isCappedHi = !!maxSelected && selected.length >= maxSelected;
    const isCappedLo = !!minSelected && selected.length <= minSelected;

    const renderItem = (option: Option) => {
        const isSelected = selectedSet.has(option.value);
        const isDisabled = disabled || (!isSelected && isCappedHi) || (isSelected && isCappedLo);
        return (
            <SearchableCheckboxItem
                key={option.value}
                value={option.value}
                label={option.label}
                extraKeywords={option.keywords}
                isSelected={isSelected}
                isDisabled={isDisabled}
                onToggle={handleToggle}
            />
        );
    };

    const input = (
        <CommandInput
            placeholder={searchPlaceholder}
            wrapperClassName={cn('bg-background border rounded-sm', inputWrapperClassName)}
            className="bg-background"
            disabled={disabled}
        />
    );

    return (
        <Command
            disablePointerSelection
            className={cn(
                'bg-transparent rounded-none',
                inputLast && 'flex-col-reverse',
                className,
            )}
        >
            {inputLast ? (
                <div className="flex flex-col gap-4">
                    <div className="px-4">{input}</div>
                    <Separator />
                </div>
            ) : (
                input
            )}
            <CommandList
                className={cn(
                    'p-1 flex-1 overflow-y-auto flex flex-col [&>[cmdk-empty]]:flex-1 [&>[cmdk-empty]]:flex [&>[cmdk-empty]]:items-center [&>[cmdk-empty]]:justify-center',
                    commandListClassName,
                )}
            >
                <CommandEmpty className={emptyWrapperClassName}>{notFoundMessage}</CommandEmpty>
                {groups.map(([name, items], index) => (
                    <Fragment key={name}>
                        {index > 0 && <CommandSeparator />}
                        <CommandGroup heading={name} className={cn('p-0', commandGroupClassName)}>
                            {items.map(renderItem)}
                        </CommandGroup>
                    </Fragment>
                ))}
                {ungrouped.length > 0 && (
                    <>
                        {groups.length > 0 && <CommandSeparator />}
                        <CommandGroup className={cn('p-0', commandGroupClassName)}>
                            {ungrouped.map(renderItem)}
                        </CommandGroup>
                    </>
                )}
            </CommandList>
        </Command>
    );
}
